import { initialAppliedFilters } from '@/features/roadmap/store/filter';
import { type AppliedFilters, type RoadmapQueryParams } from '@/features/roadmap/store/filter.types';
import { mapFiltersToParams } from '@/features/roadmap/utils/filters';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  getBasicRoadmap,
  getFollowers,
  getRoadmapCategories,
  getRoadmapClassification,
  getRoadmapDefaultFilters,
  getRoadMapFunding,
  getRoadmapLinkType,
  getRoadmapPlatforms,
  getRoadMapReleases,
  getRoadMaps,
  getRoadmapsHistory,
  getRoadmapsStatuses,
  getRoadmapStatuses,
  getRoadmapStatusResourcesHistory,
  getRoadmapsTempo,
  getRoadMapsWithTempo,
  getRoadmapTempo,
  getRoadmapTempoReportByItem,
  getRoadmapTempoReportByUser,
  getRoadmapTempoWithHoursAllocation,
  getRoadmapTypeGroup,
  getRoadMapVersionsList,
  getStatusResources,
} from '../api';
import { RoadmapDefaultFilters, type RoadmapTempoReportRequest, type RoadmapTypeGroupDTO } from '../types';

export const ROADMAP_FUNDING_KEY = 'roadmap-funding-key';
export const ROADMAP_KEY = 'roadmap-key';
export const ROADMAP_VERSION_LIST_KEY = 'roadmap-version-list-key';
export const ROADMAP_STATUS_RESOURCE_KEY = 'roadmap-status-resource-key';
export const ROADMAP_BASIC_KEY = 'roadmap-basic-key';
export const ROADMAP_TEMPO_REPORT_BY_USER_KEY = 'roadmap-tempo-report-by-user-key';
export const ROADMAP_TEMPO_REPORT_BY_ITEM_KEY = 'roadmap-tempo-report-by-item-key';
export const ROADMAP_FOLLOWERS_KEY = 'roadmap-followers-key';
export const ROADMAP_VERSION_KEY = 'roadmap-version-key';

export const useRoadmapFunding = (year: number, filterFunding: boolean, enabled = true) =>
  useQuery({
    queryKey: ['roadmap', ROADMAP_FUNDING_KEY, year, filterFunding],
    queryFn: async () => await getRoadMapFunding(year, filterFunding),
    staleTime: 1000 * 60 * 30, // 30 minutes
    enabled,
  });

export const useRoadmaps = (filters: AppliedFilters = initialAppliedFilters, enabled = true) =>
  useQuery({
    queryKey: ['roadmap', ROADMAP_KEY, filters],
    queryFn: async () => {
      const mappedFilters = mapFiltersToParams(filters);
      return getRoadMaps(mappedFilters);
    },
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
    enabled,
  });

export const useRoadmapsWithParams = (params: RoadmapQueryParams, enabled = true) =>
  useQuery({
    queryKey: ['roadmap', ROADMAP_KEY, 'params', params],
    queryFn: () => getRoadMaps(params),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
    enabled,
  });

export const useRoadmapsWithTempoParams = (params: RoadmapQueryParams, enabled = true) =>
  useQuery({
    queryKey: ['roadmap', ROADMAP_KEY, 'tempo-params', params],
    queryFn: () => getRoadMapsWithTempo(params),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
    enabled,
  });

export const useRoadmapsVersionsList = () =>
  useQuery({
    queryKey: ['roadmap', ROADMAP_VERSION_LIST_KEY],
    queryFn: getRoadMapVersionsList,
    staleTime: 1000 * 60 * 15, // 15 minutes
  });

export const useRoadmapResources = (idRoadmap: number, enabled = true) =>
  useQuery({
    queryKey: ['roadmap', ROADMAP_STATUS_RESOURCE_KEY, idRoadmap],
    queryFn: async () => await getStatusResources(idRoadmap),
    staleTime: 1000 * 60 * 10, // 30 minutes
    enabled,
  });

export function useRoadmapTypeGroup(withItems = false, initialData?: RoadmapTypeGroupDTO[]) {
  const [initialDataUpdatedAt] = useState(() => (initialData ? Date.now() : undefined));

  return useQuery({
    queryKey: ['roadmap', 'roadmap-types', withItems],
    queryFn: async () => await getRoadmapTypeGroup(withItems),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 30,
    initialData,
    initialDataUpdatedAt,
  });
}

export const useRoadmapPlatforms = () =>
  useQuery({
    queryKey: ['roadmap', 'roadmap-platforms'],
    queryFn: async () => await getRoadmapPlatforms(),
    staleTime: 1000 * 60 * 15,
  });

export function useRoadmapDefaultFilters(initialData?: RoadmapDefaultFilters) {
  const [initialDataUpdatedAt] = useState(() => (initialData ? Date.now() : undefined));

  return useQuery({
    queryKey: ['roadmap', 'default-filters'],
    queryFn: getRoadmapDefaultFilters,
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 30,
    initialData,
    initialDataUpdatedAt,
  });
}

export const useRoadmapsReleases = () =>
  useQuery({
    queryKey: ['roadmap', 'roadmaps-releases'],
    queryFn: getRoadMapReleases,
    staleTime: 1000 * 60 * 15,
  });

export const useRoadmapsStatuses = () =>
  useQuery({
    queryKey: ['roadmap', 'roadmaps-statuses'],
    queryFn: async () => await getRoadmapsStatuses(),
    staleTime: 1000 * 60 * 15,
  });

export const useBasicRoadmaps = (filters: RoadmapQueryParams | null = null, enabled = true) =>
  useQuery({
    queryKey: ['roadmap', ROADMAP_BASIC_KEY, filters],
    queryFn: () => getBasicRoadmap(filters),
    staleTime: 1000 * 60 * 15,
    enabled,
  });

export const useRoadmapStatuses = (roadmapId: number, enabled = true) =>
  useQuery({
    queryKey: ['roadmap', 'roadmap-statuses', roadmapId],
    queryFn: async () => await getRoadmapStatuses(roadmapId),
    staleTime: 1000 * 60 * 15,
    enabled,
  });

export const useRoadmapTempoReportByUser = (request: RoadmapTempoReportRequest, enabled = true) =>
  useQuery({
    queryKey: ['roadmap', ROADMAP_TEMPO_REPORT_BY_USER_KEY, request],
    queryFn: async () => await getRoadmapTempoReportByUser(request),
    staleTime: 1000 * 60 * 15,
    enabled,
  });

export const useRoadmapTempoReportByItem = (request: RoadmapTempoReportRequest, idUser?: number, enabled = true) =>
  useQuery({
    queryKey: ['roadmap', ROADMAP_TEMPO_REPORT_BY_ITEM_KEY, idUser, request],
    queryFn: async () => await getRoadmapTempoReportByItem(request, idUser),
    staleTime: 1000 * 60 * 30,
    enabled,
  });

export const useItemsWithTimeAllocation = (enabled = true) =>
  useQuery({
    queryKey: ['roadmap', ROADMAP_TEMPO_REPORT_BY_ITEM_KEY],
    queryFn: async () => await getRoadmapTempoWithHoursAllocation(),
    staleTime: 1000 * 60 * 30,
    enabled,
  });

export const useRoadmapFollowers = (roadmapId: number, enabled = true) =>
  useQuery({
    queryKey: ['roadmap', ROADMAP_FOLLOWERS_KEY, roadmapId],
    queryFn: async () => await getFollowers(roadmapId),
    staleTime: 1000 * 60 * 5,
    enabled,
  });

export const useRoadmapHistory = (roadmapId: number, field: string | null = null) =>
  useQuery({
    queryKey: ['roadmap-history', roadmapId, field],
    queryFn: async () => await getRoadmapsHistory(roadmapId, field),
    staleTime: 1000 * 60 * 5,
  });

export const useRoadmapTempo = (roadmapId: number, roadmapStatusId: number | null = null) =>
  useQuery({
    queryKey: ['roadmap-tempo', roadmapId, roadmapStatusId],
    queryFn: async () => await getRoadmapTempo(roadmapId, roadmapStatusId),
    staleTime: 1000 * 60 * 5,
  });

export const useRoadmapsTempo = () =>
  useQuery({
    queryKey: ['roadmap-tempo', 'tempo'],
    queryFn: async () => await getRoadmapsTempo(),
    staleTime: 1000 * 60 * 5,
  });

export const useRoadmapsVersions = (enabled = true) =>
  useQuery({
    queryKey: ['roadmap', ROADMAP_VERSION_KEY],
    queryFn: getRoadMapVersionsList,
    staleTime: 1000 * 60 * 15,
    enabled,
  });

export const useRoadmapClassification = () =>
  useQuery({
    queryKey: ['roadmap', 'roadmap-classification'],
    queryFn: getRoadmapClassification,
    staleTime: 1000 * 60 * 30,
  });

export const useRoadmapCategories = () =>
  useQuery({
    queryKey: ['roadmap', 'roadmap-category'],
    queryFn: getRoadmapCategories,
    staleTime: 1000 * 60 * 30,
  });

export const useRoadmapStatusResourcesHistory = (roadmapId: number) =>
  useQuery({
    queryKey: ['roadmap-status-resources-history', roadmapId],
    queryFn: async (context) => await getRoadmapStatusResourcesHistory(roadmapId, context.signal),
    staleTime: 1000 * 60 * 5,
  });

export const useRoadmapLinkType = () =>
  useQuery({
    queryKey: ['roadmap', 'link-type'],
    queryFn: getRoadmapLinkType,
    staleTime: 1000 * 60 * 30,
  });
